import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import type { StandardisedDataResponse } from 'api/resources/analytics/types';

dayjs.extend(customParseFormat);

export const getCalculatedData = (data: StandardisedDataResponse) => {
  // Totals

  const avCallDuration = Math.round(
    data.total.full_call_duration / data.total.num_conversations,
  );

  const percFuncCallsOfTotalCalls =
    data.total.function_calls.total / data.total.num_conversations;

  const percSmsSentOfTotalCalls =
    data.total.sms_sent / data.total.num_conversations;

  // Get into milliseconds
  const apiLatency = Math.round(data.total.api_calls.avg_response_time * 1000);

  const overallAverageWordsPerCall = [
    {
      name: 'Caller words',
      value: Math.round(
        data.total.ai_words_transcribed / data.total.num_conversations,
      ),
    },
    {
      name: 'Agent words',
      value: Math.round(
        data.total.ai_words_generated / data.total.num_conversations,
      ),
    },
  ];

  const smsTopicBreakdown = Object.entries(data.total.sms_topics.breakdown).map(
    ([name, value]) => ({
      name,
      value,
    }),
  );

  const apiAvailability = Object.entries(
    data.total.api_calls.status_code_breakdown,
  ).map(([name, value]) => ({
    name,
    value,
  }));

  const totalAutomatedMinutes = Math.round(
    data.total.agent_turn_total_duration / 60,
  );

  const containedCalls = data.total.num_conversations - data.total.num_handoff;
  const containmentRate = containedCalls / data.total.num_conversations;

  // Interval data

  const callDistribution = data.aggregated_data.map(data => ({
    name: dayjs(data.interval, 'YYYY-MM-DD').format('DD/MM/YYYY'),
    value: data.num_conversations,
  }));

  const callMinutesDistribution = data.aggregated_data.map(data => ({
    name: dayjs(data.interval, 'YYYY-MM-DD').format('DD/MM/YYYY'),
    'Agent minutes': Math.round(data.agent_turn_total_duration / 60),
    'Total minutes': Math.round(data.full_call_duration / 60),
  }));

  const avCallDurationDistribution = data.aggregated_data.map(data => ({
    name: dayjs(data.interval, 'YYYY-MM-DD').format('DD/MM/YYYY'),
    value: Math.round(data.full_call_duration / data.num_conversations),
  }));

  const avWordsPerConv = data.aggregated_data.map(data => ({
    name: dayjs(data.interval, 'YYYY-MM-DD').format('DD/MM/YYYY'),
    'Words per conversation': Math.round(
      (data.ai_words_transcribed + data.ai_words_generated) /
        data.num_conversations || 0,
    ),
    'AI Words per conversation': Math.round(
      data.ai_words_generated / data.num_conversations || 0,
    ),
  }));

  const functionCallsPerDay = data.aggregated_data.map(data => ({
    name: dayjs(data.interval, 'YYYY-MM-DD').format('DD/MM/YYYY'),
    value: data.function_calls.total,
  }));

  const apiPerformanceAndFailure = data.aggregated_data.map(data => {
    return {
      name: dayjs(data.interval, 'YYYY-MM-DD').format('DD/MM/YYYY'),
      ...data.api_calls.status_code_breakdown,
    };
  });

  const smsSentVsFailed = data.aggregated_data.map(data => ({
    name: dayjs(data.interval, 'YYYY-MM-DD').format('DD/MM/YYYY'),
    'SMS sent': data.sms_sent,
    'SMS failed': data.sms_failed,
  }));

  const containmentRateDistribution = data.aggregated_data.map(data => {
    const containedCalls = data.num_conversations - data.num_handoff;

    return {
      name: dayjs(data.interval, 'YYYY-MM-DD').format('DD/MM/YYYY'),
      value: containedCalls / data.num_conversations,
    };
  });

  return {
    apiAvailability,
    apiLatency,
    apiPerformanceAndFailure,
    avCallDuration,
    avCallDurationDistribution,
    avWordsPerConv,
    callDistribution,
    callMinutesDistribution,
    containmentRate,
    containmentRateDistribution,
    functionCallsPerDay,
    overallAverageWordsPerCall,
    percFuncCallsOfTotalCalls,
    percSmsSentOfTotalCalls,
    smsSentVsFailed,
    smsTopicBreakdown,
    totalApiRequestsSent: data.total.api_calls.total,
    totalAutomatedMinutes,
    totalCalls: data.total.num_conversations,
  };
};

export const DEFAULT_DASHBOARD_DATE_RANGE = 3; // in months

export const getStandardDashboardDefaultStartDate = () => {
  const today = dayjs();
  return today.subtract(DEFAULT_DASHBOARD_DATE_RANGE, 'months').toDate();
};

export const getStandardDashboardDefaultEndDate = () => {
  const today = dayjs();
  return today.toDate();
};
